import { observable, action } from 'mobx';
import { ControlPosition } from 'react-draggable';
import config from '../config';
import { VoipCredentials } from './session/VoipCredentials';
import { notifications } from './notifications/NotificationStore';
import jwtDecode from 'jwt-decode';
import { StoredJwt } from './session/StoredJwt';
import { Jwt } from './services/api/Jwt';
import { JwtObject } from './session/JwtObject';
import { Variant } from '../components/notifications/Variant';
import { ApiService } from './services/api';
import { AuthorizedUser } from './session/AuthorizedUser';
import { ResponseStatus } from './services/api/response/ResponseStatus';
import { StorageStore } from './storage/StorageStore';

export class Session {
    public static LS_BEARER_KEY = 'jwt';
    public static LS_CREDENTIALS_KEY = 'credentials';
    public static LS_WIDGET_DATA = 'widget_data';
    public static LS_WIDGET_POSITION = 'widget_position'
    private api: ApiService;
    @observable private _user?: AuthorizedUser;
    @observable private _voipCredentials?: VoipCredentials;
    @observable private _loggedIn = false;
    public isWidget = true;
    @observable public usesExpired = true;

    public constructor() {
        this.api = new ApiService();
    }

    public set widget(value: boolean) {
        this.isWidget = value;
    }

    public get user(): AuthorizedUser | undefined {
        return this._user;
    }

    public get voipCredentials(): VoipCredentials | undefined {
       return this._voipCredentials;
    }

    public get sessionValid(): boolean {
        const storage = StorageStore.sessionOrLocal(Session.LS_BEARER_KEY);
        const token = storage?.getItem<StoredJwt>(Session.LS_BEARER_KEY, true);

        return !!token;
    }

    public get loggedIn(): boolean {
        return this._loggedIn;
    }

    public set loggedIn(value: boolean) {
        this._loggedIn = value;
    }

    public async login(
        username: string,
        password: string
    ): Promise<string> {
        const response = await fetch(
            `${config.api.baseUrl}oauth/token`,
            this.makeFetchInit({
                body: JSON.stringify({
                    grant_type: config.api.grant_type,
                    client_id: config.api.client_id,
                    client_secret: config.api.secret_key,
                    username,
                    password,
                    scope: '*'
                })
            })
        );

        if (response.status === 200) {
            notifications.clear();
            const responseJson = await response.json();
            new StorageStore(sessionStorage).setItem(
                Session.LS_BEARER_KEY,
                Session.makeStoredJwt(responseJson)
            );

            if (await this.fetchUser()) {
                const credentials = await this.fetchCredentials();

                if (credentials) {
                    return 'success';
                }
            }

            this.logout();

            return 'no_credentials';
        }

        return 'incorrect';
    }

    public setUser(user: AuthorizedUser): Session {
        this._user = user;

        return this;
    }

    @action
    public updateUser(key: keyof AuthorizedUser, value: any): void {
        if (this._user) {
            this._user[key] = value;
        }
    }

    public async fetchUser(): Promise<boolean> {
        const response = await this.api.get('api/v2/userinfo');

        if (response.getStatus() === ResponseStatus.OK) {
            this.setUser(response.getData() as AuthorizedUser);

            return true;
        }

        return false;
    }

    public logout(): void {
        this._user = undefined;
        this._voipCredentials = undefined;
        this._loggedIn = false;
    }

    public async fetchCredentials(): Promise<boolean> {
        const storage = StorageStore.sessionOrLocal(Session.LS_BEARER_KEY);

        if(storage?.getItem(Session.LS_BEARER_KEY, true)) {
            const response = await this.api.get<VoipCredentials>('api/v2/user/voip/configuration')

            if (response.getStatus() === ResponseStatus.OK) {
                const responseJson = response.getData() as VoipCredentials;

                if (!responseJson || (Array.isArray(responseJson) && !responseJson.length)) {
                    return false;
                } else {
                    this._voipCredentials = responseJson;
                    return true;
                }
            }
        }
        return false;
    }

    private makeFetchInit(config?: RequestInit): RequestInit {
        const initObject: RequestInit = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },

        }

        return { ...initObject, ...config };
    }

    private getAccessHeaders(): { Authorisation: string } | {} {
        const storage = StorageStore.sessionOrLocal(Session.LS_BEARER_KEY);
        const token = storage?.getItem<StoredJwt>(Session.LS_BEARER_KEY, true);

        if (token) {
            return {
                Authorization: `Bearer ${token.access_token}`
            };
        }

        return {};
    }

    public setWidgetSettings(name: string, data: any): void {
        if (name === 'position') {
            return this.setPositionSettings(data as ControlPosition);
        }

        const storage = new StorageStore(sessionStorage);

        let settings = storage.getItem<Record<string, string>>(Session.LS_WIDGET_DATA, true) || {};

        settings[name] = data;
        storage.setItem(Session.LS_WIDGET_DATA, settings);
    }

    private setPositionSettings(data: ControlPosition): void {
        new StorageStore(sessionStorage).setItem(Session.LS_WIDGET_POSITION, data);
    }

    private getPositionSettings(): ControlPosition | null {
        return new StorageStore(sessionStorage).getItem<ControlPosition>(Session.LS_WIDGET_POSITION, true);
    }

    public getWidgetSettings(name: string): any {
        if (name === 'position') {
            return this.getPositionSettings()
        }

        const data = new StorageStore(sessionStorage).getItem<Record<string, any>>(Session.LS_WIDGET_DATA, true);

        if (data) {
            return data[name]
        }

        return null;
    }

    public get jwt(): StoredJwt | null {
        const storage = StorageStore.sessionOrLocal(Session.LS_BEARER_KEY);
        const token = storage?.getItem<StoredJwt>(Session.LS_BEARER_KEY, true);

        if (!token) {
            return null;
        }

        try {
            jwtDecode((token as StoredJwt).access_token);

            return token;
        } catch (e) {
            return null;
        }
    }

    public isExpired(): boolean {
        const storage = StorageStore.sessionOrLocal(Session.LS_BEARER_KEY);
        const token = storage?.getItem<StoredJwt>(Session.LS_BEARER_KEY, true);

        if (token) {
            try {
                const jwt = jwtDecode<Jwt>((token as StoredJwt).access_token);

                return !jwt.exp || (jwt.exp * 1000) < Date.now();
            } catch (e) {
                return true;
            }
        }

        return true;
    }

    public static makeStoredJwt(jwt: JwtObject): StoredJwt {
        return {
            access_token: jwt.access_token,
            refresh_token: jwt.refresh_token
        };
    }

    public addExpiredMessage(): void {
        notifications.addNotification({
            variant: Variant.DANGER,
            message: 'Sessie verlopen. Log opnieuw in'
        })
    }

    public addCantCallMessage(): void {
        notifications.addNotification({
            variant: Variant.DANGER,
            message: 'Geen mogelijkheid tot bellen'
        })
    }
}

export const session = new Session();
